import React, { useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Globe, Check } from "lucide-react";

const LANGUAGES = [
  { code: "uk", name: "Українська", flag: "🇺🇦" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

export default function LanguageSelectPage({ onComplete }) {
  const { lang, switchLang, t } = useLanguage();
  const autoTimer = useRef(null);

  const handleSelect = (code) => {
    switchLang(code);
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => onComplete(), 600);
  };

  const handleContinue = () => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    onComplete();
  };

  // Get greeting in all languages for visual effect
  const greetings = {
    uk: "Ласкаво просимо",
    en: "Welcome",
    ru: "Добро пожаловать"
  };

  return (
    <div className="fixed inset-0 bg-[#FAFAF8] flex flex-col z-[10002]" data-testid="language-select-page">
      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-[#7B9E7F]/10 flex items-center justify-center mb-6">
          <Globe className="w-10 h-10 text-[#7B9E7F]" strokeWidth={1.5} />
        </div>
        
        <h1 className="font-heading text-3xl text-[#3D4035] text-center mb-2">
          {greetings[lang] || t("selectLanguage")}
        </h1>
        <p className="font-body text-base text-[#A5A58D] text-center mb-2">
          {t("selectLanguage")}
        </p>
        <p className="font-body text-xs text-[#A5A58D]/70 text-center mb-8">
          {t("languageCanChange") || "Ви зможете змінити мову в налаштуваннях"}
        </p>

        {/* Language Options */}
        <div className="w-full max-w-sm space-y-3">
          {LANGUAGES.map(({ code, name, flag }) => (
            <button
              key={code}
              data-testid={`lang-option-${code}`}
              onClick={() => handleSelect(code)}
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                lang === code
                  ? "border-[#7B9E7F] bg-[#7B9E7F]/5"
                  : "border-[#E8E5E0] bg-white hover:border-[#A5A58D]"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{flag}</span>
                <span className={`font-body font-semibold text-lg ${lang === code ? "text-[#4A6B4E]" : "text-[#3D4035]"}`}>
                  {name}
                </span>
              </div>
              {lang === code && (
                <div className="w-6 h-6 rounded-full bg-[#7B9E7F] flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" strokeWidth={2} />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Continue Button */}
      <div className="px-6 pb-8 pt-4">
        <button
          data-testid="language-continue-btn"
          onClick={handleContinue}
          className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-lg hover:bg-[#6B8E6F] transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20"
        >
          {t("continueBtn")}
        </button>
      </div>
    </div>
  );
}
